import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getToken, setToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined=loading, null=guest, object=logged in
  const [initializing, setInitializing] = useState(true);

  const refresh = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      return;
    }
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch {
      setToken("");
      setUser(null);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await refresh();
      setInitializing(false);
    })();
    const onLogout = () => setUser(null);
    window.addEventListener("auth:logout", onLogout);
    return () => window.removeEventListener("auth:logout", onLogout);
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    setToken(r.data.token);
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const impersonate = useCallback(async (userId) => {
    const r = await api.post(`/auth/impersonate/${userId}`);
    // Preserve original admin token so we can restore locally if backend is unreachable
    try {
      const current = getToken();
      if (current) localStorage.setItem("bordromat_admin_token", current);
    } catch {}
    setToken(r.data.token);
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const extendImpersonation = useCallback(async () => {
    const r = await api.post("/auth/impersonate/extend");
    setToken(r.data.token);
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const stopImpersonation = useCallback(async () => {
    try {
      const r = await api.post("/auth/stop-impersonation");
      setToken(r.data.token);
      setUser(r.data.user);
    } catch (e) {
      // Fallback: restore locally-stored admin token
      try {
        const admin = localStorage.getItem("bordromat_admin_token");
        if (admin) {
          setToken(admin);
          const me = await api.get("/auth/me");
          setUser(me.data);
        }
      } catch (e2) { console.warn("Yönetici oturumu geri yüklenemedi", e2); }
    } finally {
      try { localStorage.removeItem("bordromat_admin_token"); } catch {}
    }
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.warn("Sunucuda çıkış yapılamadı", e); }
    try { localStorage.removeItem("bordromat_admin_token"); } catch {}
    setToken("");
    setUser(null);
  }, []);

  const role = user?.role || null;
  const perms = user?.permissions || {};
  const value = {
    user, role,
    permissions: perms,
    isAdmin: role === "admin",
    isMuhasebeci: role === "muhasebeci" || role === "yonetici",
    isYonetici: role === "yonetici",
    canManageStatements: role === "admin" || role === "yonetici",
    isMuhasebeStj: role === "muhasebe_stj",
    isJuniorMuhasebe: role === "junior_muhasebe",
    isPersonel: role === "personel",
    canSeeMoney: !!perms.canSeeMoney,
    canEditMoney: !!perms.canEditMoney,
    canSeePrimRate: !!perms.canSeePrimRate,
    canSeeKidem: perms.canSeeKidem !== false,
    canSeeTotals: perms.canSeeTotals !== false,
    canSeeKpiCards: !!perms.canSeeKpiCards,
    canManageUsers: !!perms.canManageUsers,
    allowedTabs: perms.tabs || [],
    canAccess: (tab) => (perms.tabs || []).includes(tab),
    impersonatedBy: user?.impersonated_by || null,
    isImpersonating: !!user?.impersonated_by,
    impersonationExpiresAt: user?.impersonation_expires_at || null,
    initializing,
    login,
    logout,
    impersonate,
    extendImpersonation,
    stopImpersonation,
    refresh,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

// Helper: mask money values for muhasebeci
export function useMoneyMask() {
  const { canSeeMoney } = useAuth();
  return (formatted) => (canSeeMoney ? formatted : "•••••");
}
